// Confidence badge, Origin badge, OCR field input, and Line Item row.
// Extracted from pages/InvoiceOCR.jsx — purely presentational, no business
// logic, no data fetching. Keep this file dumb: only props in, JSX out.
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { CheckCircle2, Trash2 } from 'lucide-react';
import { cn } from '../../lib/utils';

export const ConfidenceBadge = ({ confidence, showValue = true }) => {
  let colorClass = 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400';
  let label = 'High';

  if (confidence < 0.70) {
    colorClass = 'bg-rose-100 text-rose-800 dark:bg-rose-900/30 dark:text-rose-400';
    label = 'Low';
  } else if (confidence < 0.85) {
    colorClass = 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400';
    label = 'Medium';
  }

  return (
    <Badge variant="secondary" className={cn('text-xs', colorClass)}>
      {label}{showValue && ` (${Math.round(confidence * 100)}%)`}
    </Badge>
  );
};

// Origin badge — tells the user at a glance whether the value came from the
// AI extraction or whether they (or another teammate) edited it post-extract.
// Drives the "AI vs human" audit trail requested by accounting customers.
export const OriginBadge = ({ edited }) => {
  if (edited) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300"
        title="This value was edited by a person after AI extraction"
        data-testid="ocr-origin-edited"
      >
        ✓ Edited
      </span>
    );
  }
  return (
    <span
      className="inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-md bg-violet-100 text-violet-700 dark:bg-violet-900/40 dark:text-violet-300"
      title="This value was extracted by AI vision and has not been edited"
      data-testid="ocr-origin-ai"
    >
      AI
    </span>
  );
};

// Field with confidence indicator
export const OCRField = ({
  label,
  value,
  confidence,
  onChange,
  requiresConfirmation,
  confirmed,
  onConfirm,
  type = "text",
  placeholder,
  disabled = false,
  edited = false,
  originalValue,
}) => {
  const isLowConfidence = confidence < 0.70;

  return (
    <div className="space-y-1.5">
      <div className="flex items-center justify-between">
        <Label className="text-sm font-medium flex items-center gap-2">
          {label}
          <OriginBadge edited={edited} />
        </Label>
        {confidence !== undefined && (
          <ConfidenceBadge confidence={confidence} />
        )}
      </div>
      <div className="relative">
        <Input
          type={type}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          disabled={disabled}
          className={cn(
            isLowConfidence && !confirmed && 'border-amber-500 bg-amber-50/50 dark:bg-amber-900/10',
            confirmed && 'border-emerald-500 bg-emerald-50/50 dark:bg-emerald-900/10'
          )}
          data-testid={`ocr-field-${label.toLowerCase().replace(/\s+/g, '-')}`}
        />
        {isLowConfidence && !confirmed && (
          <Button
            size="sm"
            variant="ghost"
            className="absolute right-1 top-1 h-7 text-xs text-amber-600 hover:text-amber-700"
            onClick={onConfirm}
            data-testid={`confirm-field-${label.toLowerCase().replace(/\s+/g, '-')}`}
          >
            <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
            Confirm
          </Button>
        )}
        {confirmed && (
          <CheckCircle2 className="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 text-emerald-500" />
        )}
      </div>
      {isLowConfidence && !confirmed && (
        <p className="text-xs text-amber-600">AI Buck needs verification—please confirm this field</p>
      )}
    </div>
  );
};

// Line item row
export const LineItemRow = ({ item, index, onChange, onRemove, canRemove }) => {
  return (
    <div className="grid grid-cols-12 gap-2 items-end p-2 bg-muted/30 rounded-lg">
      <div className="col-span-5">
        <Label className="text-xs text-muted-foreground">Description</Label>
        <Input
          value={item.description || ''}
          onChange={(e) => onChange(index, 'description', e.target.value)}
          placeholder="Item description"
          className="text-sm"
          data-testid={`line-item-${index}-description`}
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs text-muted-foreground">Qty</Label>
        <Input
          type="number"
          value={item.quantity || 1}
          onChange={(e) => onChange(index, 'quantity', parseFloat(e.target.value) || 0)}
          className="text-sm font-mono"
          min="0"
          step="1"
          data-testid={`line-item-${index}-quantity`}
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs text-muted-foreground">Unit Price</Label>
        <Input
          type="number"
          value={item.unit_price || 0}
          onChange={(e) => onChange(index, 'unit_price', parseFloat(e.target.value) || 0)}
          className="text-sm font-mono"
          min="0"
          step="0.01"
          data-testid={`line-item-${index}-unit-price`}
        />
      </div>
      <div className="col-span-2">
        <Label className="text-xs text-muted-foreground">Amount</Label>
        <Input
          type="number"
          value={item.amount || 0}
          onChange={(e) => onChange(index, 'amount', parseFloat(e.target.value) || 0)}
          className="text-sm font-mono"
          min="0"
          step="0.01"
          data-testid={`line-item-${index}-amount`}
        />
      </div>
      <div className="col-span-1 flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-muted-foreground hover:text-destructive"
          onClick={() => onRemove(index)}
          disabled={!canRemove}
          data-testid={`remove-line-item-${index}`}
        >
          <Trash2 className="w-4 h-4" />
        </Button>
      </div>
    </div>
  );
};
